import { useState } from 'react';
import { siteInfo } from '@/mocks/siteInfo';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      setTimeout(() => { setFormStatus('idle'); setFormMessage(''); }, 5000);
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q7jjknlsngrm4llst0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络连接异常，请稍后重试。');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于成91',
    description: '了解成91平台的发展历程、品牌信誉与联系方式',
    url: 'https://cheng91.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: '成91官方平台',
      description: siteInfo.description,
      foundingDate: siteInfo.founded,
      contactPoint: {
        '@type': 'ContactPoint',
        email: siteInfo.contact.email,
        telephone: siteInfo.contact.phone,
        contactType: 'customer service',
        availableLanguage: 'Chinese',
        hoursAvailable: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
            opens: '09:00',
            closes: '22:00',
          },
        ],
      },
    },
  };

  return (
    <>
      <PageSEO
        title="关于我们 - 成91官方平台"
        description="了解成91平台的发展历程、品牌信誉与联系方式。成91是备受用户关注的热门平台，致力于为用户提供优质、便捷的在线服务体验。"
        keywords="成91,关于成91,成91品牌,成91联系方式,成91客服"
        canonicalUrl="/about"
      />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-8 bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于 <span className="text-primary-500">成91</span>
            </h1>
            <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              {siteInfo.description}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-12">
              平台数据
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {siteInfo.reputation.map((item) => (
                <div key={item.label} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-accent-500 mb-2">{item.value}</div>
                  <div className="text-sm text-foreground-600">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-12">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2 hidden md:block"></div>
              <div className="space-y-8">
                {siteInfo.milestones.map((milestone, index) => (
                  <div key={milestone.year} className={`flex flex-col md:flex-row gap-4 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                      <div className={`p-5 rounded-xl bg-background-100 border border-background-200/70`}>
                        <div className="text-sm text-foreground-700">{milestone.event}</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-accent-500 text-background-50 flex items-center justify-center font-heading font-bold text-sm flex-shrink-0">
                        {milestone.year.slice(2)}
                      </div>
                    </div>
                    <div className="flex-1"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-12">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">联系方式</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">电子邮箱</div>
                      <div className="text-sm text-foreground-600">{siteInfo.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">客服电话</div>
                      <div className="text-sm text-foreground-600">{siteInfo.contact.phone}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">工作时间</div>
                      <div className="text-sm text-foreground-600">{siteInfo.contact.workingHours}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">在线留言</h3>
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="请输入您的留言内容（500字以内）"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                  </div>
                  <div className="form-honeypot">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </span>
                    ) : '提交留言'}
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-lg bg-accent-50 text-accent-700 text-sm text-center">
                      {formMessage}
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm text-center">
                      {formMessage}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}